import {
  Button,
  Card,
  CardHeader,
  ServerErrorFallbackCard,
  Text,
} from "@/components/ui";
import { PageHighlightCards } from "@/components/ui/PageHighlightsCards/PageHighlightCards";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/shadcn/Select/Select";
import { useUser } from "@/components/UserProvider";
import { useExpensesViewModel } from "@/hooks/matter-view-models/use-expenses-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { ExpenseIcon } from "@/lib/utils/iconUtils";
import { calculateExpenseTotals } from "@/lib/utils/matters/expense-utils";
import { ErrorBoundary } from "@sentry/react";
import { useCallback, useMemo, useState } from "react";
import { ExpensesCardView } from "./ExpensesCardView";
import { MatterExpensesTable } from "./MatterExpensesTable";

type ExpenseViewType = "table" | "card";

interface MatterExpensesProps {
  matter: Matter;
  searchExpenseId?: string;
}

const MatterExpensesDangerous = ({
  matter,
  searchExpenseId,
}: MatterExpensesProps) => {
  const { hasPagePermission, hasActionPermission } = useUser();
  const hasViewPermission = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailCosts,
  );
  const [currentView, setCurrentView] = useState<ExpenseViewType>("card");
  const expensesViewModel = useExpensesViewModel(matter.id);
  const { openExpenseModal } = useGlobalModal();
  const canViewRelatedMatters = Boolean(true);
  const hasRelatedMatters =
    canViewRelatedMatters && matter.relatedMatterConnections.length > 0;

  const handleCreateClick = () => {
    openExpenseModal({
      matterId: matter.id,
      expenseId: undefined,
      editing: false,
      initialTab: "details",
    });
  };

  const handleSplitClick = () => {
    openExpenseModal({
      matterId: matter.id,
      expenseId: undefined,
      editing: false,
      initialTab: "details",
      splitMode: true,
    });
  };

  const handleViewChange = useCallback((view: ExpenseViewType) => {
    setCurrentView(view);
  }, []);

  const { totalExpenses, totalPaid, totalRefunded, totalBalanceRemaining } =
    calculateExpenseTotals(matter.expenses);

  const pageHighlightCards = useMemo(() => {
    return [
      {
        title: "Total costs",
        value: totalExpenses,
        isCurrency: true,
      },
      {
        title: "Total paid",
        value: totalPaid,
        isCurrency: true,
      },
      {
        title: "Total refunded",
        value: totalRefunded,
        isCurrency: true,
      },
      {
        title: "Balance remaining",
        value: totalBalanceRemaining,
        isCurrency: true,
      },
    ];
  }, [totalBalanceRemaining, totalExpenses, totalPaid, totalRefunded]);

  return (
    <div className="flex flex-col gap-y-4 p-6">
      <Text as="h1" variant="title">
        Costs
      </Text>
      {hasViewPermission && (
        <PageHighlightCards items={pageHighlightCards} variant="summary" />
      )}

      <div className="flex w-full flex-1 grow flex-wrap items-center justify-between gap-x-2 gap-y-2">
        <Text variant="title">
          {hasViewPermission ? `Costs (${matter.expenses.length})` : "Costs"}
        </Text>
        <div className="flex flex-wrap items-center gap-x-2">
          {hasViewPermission && (
            <Select
              value={currentView}
              onValueChange={(value) => {
                handleViewChange(value as ExpenseViewType);
              }}
            >
              <SelectTrigger className="border-border w-32 focus:ring-0 focus:ring-offset-0">
                <Text variant="subHeading" as="span">
                  View
                </Text>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="table">Table</SelectItem>
                <SelectItem value="card">Cards</SelectItem>
              </SelectContent>
            </Select>
          )}
          {hasActionPermission(
            ACTION_PERMISSION_KEYS.matterExpense,
            ACTION_PERMISSION_VALUES.create,
          ) && (
            <>
              {hasRelatedMatters && (
                <Button onClick={handleSplitClick} variant="light-primary">
                  <ExpenseIcon className="h-4 w-4" />
                  Split costs
                </Button>
              )}
              <Button
                onClick={handleCreateClick}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <ExpenseIcon className="h-4 w-4" />
                Create new cost
              </Button>
            </>
          )}
        </div>
      </div>

      {!hasViewPermission && (
        <Card className="w-full border-2">
          <CardHeader className="space-y-1">
            <Text variant="subHeading">
              You do not have permission to view costs.
            </Text>
          </CardHeader>
        </Card>
      )}
      {hasViewPermission && (
        <>
          {matter.expenses.length === 0 && (
            <Card className="w-full border-2 border-dashed">
              <CardHeader className="space-y-1 text-center">
                <Text
                  variant="body"
                  className="text-muted-foreground font-semibold"
                >
                  You haven&apos;t added a cost yet.
                </Text>
                <Text variant="subHeading">
                  Create your first by clicking &apos;Create new cost&apos;
                </Text>
              </CardHeader>
            </Card>
          )}
          {matter.expenses.length > 0 &&
            (currentView === "table" ? (
              <MatterExpensesTable
                matter={matter}
                searchExpenseId={searchExpenseId}
              />
            ) : (
              <ExpensesCardView expenses={expensesViewModel} matter={matter} />
            ))}
        </>
      )}
    </div>
  );
};

export const MatterExpenses = (props: MatterExpensesProps) => (
  <ErrorBoundary
    fallback={
      <div className="p-6">
        <ServerErrorFallbackCard />
      </div>
    }
  >
    <MatterExpensesDangerous {...props} />
  </ErrorBoundary>
);
